import React, {useCallback, useState} from 'react';
import cx from 'classnames';
import {useTranslation} from 'react-i18next';

import type {RenderEffects} from '@pexip/media-processor';
import {
    IconTypes,
    usePortrait,
    useLandscape,
    Button,
    useOutOfViewNavigation,
    Icon,
    InteractiveElement,
    NotificationTooltip,
} from '@pexip/components';

import styles from './EffectsManager.module.scss';
import {EffectItem, type EffectItemProps} from '../EffectItem/EffectItem.view';
import {isMobileDevice as getIsMobileDevice} from '../../utils';
import {TestId} from '../../../test/testIds';
import {EFFECTS_MANAGER_NAVIGATION_MOVE_DISTANCE} from '../../constants';

type ImageType = 'default' | 'user'; // overlay image that is app default or user uploaded

export const EffectsManagerView: React.FC<{
    updatingPreviewVideo?: boolean;
    isSaving: boolean;
    selectedEffect: RenderEffects;
    selectedImageUrl?: string;
    selectedBlurAmount?: number;
    backgroundBlurAmountLow: number;
    backgroundBlurAmountHigh: number;
    imageUrls: {imageUrl: string; type: ImageType}[];
    totalAllowedImages: number;
    acceptedImageFormats: string;
    imageError?: {title: string; body: string};
    clearImageError?: () => void;
    onBackgroundBlurClick: (amount: number) => void;
    onNoneClick: () => void;
    onFileUploadChange: React.ChangeEventHandler<HTMLInputElement>;
    onImageClick: (imageUrl: string) => void;
    onRemoveImageClick: (imageUrl: string) => void;
    onCancel: () => void;
    onSave: (e: React.SyntheticEvent<HTMLElement>) => void;
}> = ({
    updatingPreviewVideo,
    isSaving,
    selectedEffect,
    selectedImageUrl,
    selectedBlurAmount,
    backgroundBlurAmountLow,
    backgroundBlurAmountHigh,
    imageUrls,
    imageError,
    clearImageError,
    totalAllowedImages = 1,
    acceptedImageFormats,
    onBackgroundBlurClick,
    onNoneClick,
    onFileUploadChange,
    onImageClick,
    onRemoveImageClick,
    onCancel,
    onSave,
}) => {
    const {t} = useTranslation();
    const isMobileDevice = getIsMobileDevice();
    const isDesktop = !isMobileDevice;
    const isPortrait = usePortrait();
    const isLandscape = useLandscape();
    const isMobilePortrait = isMobileDevice && isPortrait;
    const isMobileLandscape = isMobileDevice && isLandscape;
    const showUploadButton = imageUrls.length < totalAllowedImages;
    const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);

    const setRootRef = useCallback((node: HTMLDivElement | null) => {
        setRootEl(node);
    }, []);

    let effectItemSize: EffectItemProps['size'];

    if (isDesktop) {
        effectItemSize = 'smallSquare';
    } else if (isMobilePortrait) {
        effectItemSize = 'largePortrait';
    } else if (isMobileLandscape) {
        effectItemSize = 'largeSquare';
    }

    const {moveLeft, moveRight, navigationView, hasOverflowingContent} =
        useOutOfViewNavigation({
            moveBy: EFFECTS_MANAGER_NAVIGATION_MOVE_DISTANCE,
            root: rootEl,
            isDisabled: false,
            containerClassName: cx(styles.footerInner, {
                [styles.largeGap]: isMobileLandscape,
                [styles.mediumGap]: isMobilePortrait,
                [styles.smallGap]: isDesktop,
            }),
            content: (
                <>
                    <EffectItem
                        onClick={onNoneClick}
                        isSelected={selectedEffect === 'none'}
                        size={effectItemSize}
                        icon={IconTypes.IconBlock}
                        effectName={t(
                            'media.effect.no-background',
                            'No background',
                        )}
                        data-testid={TestId.ButtonBackgroundEffectNone}
                    />
                    <EffectItem
                        onClick={() =>
                            onBackgroundBlurClick(backgroundBlurAmountLow)
                        }
                        isSelected={
                            selectedEffect === 'blur' &&
                            selectedBlurAmount === backgroundBlurAmountLow
                        }
                        size={effectItemSize}
                        icon={IconTypes.IconBackgroundBlurLight}
                        effectName={t('media.effect.light-blur', 'Light blur')}
                        data-testid={TestId.ButtonBackgroundEffectBlurLow}
                    />
                    <EffectItem
                        onClick={() =>
                            onBackgroundBlurClick(backgroundBlurAmountHigh)
                        }
                        isSelected={
                            selectedEffect === 'blur' &&
                            selectedBlurAmount === backgroundBlurAmountHigh
                        }
                        size={effectItemSize}
                        icon={IconTypes.IconBackgroundBlurStrong}
                        effectName={t(
                            'media.effect.strong-blur',
                            'Strong blur',
                        )}
                        data-testid={TestId.ButtonBackgroundEffectBlurHigh}
                    />
                    {showUploadButton && (
                        <EffectItem
                            acceptedImageFormats={acceptedImageFormats}
                            onFileUploadChange={onFileUploadChange}
                            isSelected={false}
                            size={effectItemSize}
                            icon={IconTypes.IconImport}
                            effectName={t(
                                'media.effect.upload-background',
                                'Upload background',
                            )}
                            data-testid={TestId.ButtonBackgroundEffectUpload}
                        />
                    )}
                    {imageError && (
                        <NotificationTooltip
                            shouldCloseOnOutsideClick
                            controlledIsVisible={true}
                            controlledOnChange={() => {
                                clearImageError?.();
                            }}
                            header={imageError.title}
                            text={imageError.body}
                            position="topCenter"
                            closeButtonAriaLabel={t(
                                'common.close-tooltip',
                                'Close tooltip',
                            )}
                        >
                            <EffectItem
                                isImageError
                                onClick={() => {
                                    clearImageError?.();
                                }}
                                showTooltip={false}
                                size={
                                    isDesktop
                                        ? 'smallLandscape'
                                        : effectItemSize
                                }
                                effectName={imageError.title}
                                isSelected={false}
                            />
                        </NotificationTooltip>
                    )}
                    {imageUrls.map(({imageUrl, type}, imageIndex) => {
                        const isSelected =
                            selectedEffect === 'overlay' &&
                            selectedImageUrl === imageUrl;
                        return (
                            <EffectItem
                                key={imageUrl}
                                showTooltip={false}
                                size={
                                    isDesktop
                                        ? 'smallLandscape'
                                        : effectItemSize
                                }
                                imageUrl={imageUrl}
                                isSelected={isSelected}
                                onClick={() => {
                                    onImageClick(imageUrl);
                                }}
                                data-testid={`${TestId.ButtonBackgroundEffectImage}-${imageIndex}`}
                                endContent={isHovering => {
                                    return type === 'user' &&
                                        ((isMobileDevice && isSelected) ||
                                            (!isMobileDevice && isHovering)) ? (
                                        <Button
                                            colorScheme="light"
                                            className={styles.removeImage}
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                onRemoveImageClick(imageUrl);
                                            }}
                                            data-testid={
                                                TestId.ButtonBackgroundEffectRemoveImage
                                            }
                                            size="compact"
                                            modifier="disc"
                                            variant="secondary"
                                            noEnhancerPadding
                                            aria-label={t(
                                                'settings.remove-image',
                                                'Remove image',
                                            )}
                                            enhancerStart={
                                                <Icon
                                                    source={IconTypes.IconClose}
                                                    size="compact"
                                                />
                                            }
                                        />
                                    ) : null;
                                }}
                            />
                        );
                    })}
                </>
            ),
        });

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Button
                    colorScheme="light"
                    size="small"
                    onClick={() => {
                        onCancel();
                    }}
                    variant="secondary"
                    data-testid={TestId.ButtonBackgroundEffectCancel}
                    isDisabled={isSaving || updatingPreviewVideo}
                >
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    onClick={onSave}
                    variant="primary"
                    data-testid={TestId.ButtonBackgroundEffectSave}
                    isLoading={isSaving || updatingPreviewVideo}
                    isDisabled={isSaving}
                >
                    {t('common.save', 'Save')}
                </Button>
            </div>
            <div
                className={cx(styles.footer, {
                    'justify-content-center': !hasOverflowingContent,
                })}
                ref={setRootRef}
            >
                {moveLeft && (
                    <InteractiveElement
                        className={cx(styles.leftNavigationButton)}
                        onClick={moveLeft}
                        data-testid={TestId.ButtonBackgroundEffectNavigateLeft}
                    >
                        <Icon
                            colorScheme="dark"
                            source={IconTypes.IconChevronLeft}
                            size="medium"
                        />
                    </InteractiveElement>
                )}
                {navigationView}
                {moveRight && (
                    <InteractiveElement
                        className={cx(styles.rightNavigationButton)}
                        onClick={moveRight}
                        data-testid={TestId.ButtonBackgroundEffectNavigateRight}
                    >
                        <Icon
                            colorScheme="dark"
                            source={IconTypes.IconChevronRight}
                            size="medium"
                        />
                    </InteractiveElement>
                )}
            </div>
        </div>
    );
};
